import { Download, InfoOutlined } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  RefreshButton,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EndPoints } from '../../common/enums/enums';
import DownloadURL from '../../common/helpers/DownloadURL';
import { amplifyApi } from '../../hooks/amplifyApi';
import { selectUserPerson } from '../../redux/selectors/userDataSelectors';
import InfoTableDialog from './InfoTableDialog';
import MultiHeaderTable from './MultiHeaderTable';

export default function DealerShipSummaryReport() {
  const { t } = useTranslation('reports');

  const userPerson = useSelector(selectUserPerson);
  const dealershipGroupId = userPerson?.company?.companyId;

  const [reportsData, setReportsData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [showInfoTable, setShowInfoTable] = useState(false);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const [showSnackbar, setShowSnackbar] = useState({
    open: false,
    text: '',
    type: '',
  });

  const toNumber = val => Number(val) || 0;

  const updateTotals = (totals, item) => {
    totals.used_photos += toNumber(item.usedVehiclesWithoutPhotos);
    totals.used_veh += toNumber(item.usedVehicleCount);
    totals.uc_det_queue += toNumber(item.uc_details_in_queue);
    totals.uc_det_comp += toNumber(item.uc_details_completed);
    totals.new_photos += toNumber(item.newVehiclesWithoutPhotos);
    totals.new_veh += toNumber(item.newVehicleCount);
    totals.used_comp_yest += toNumber(item.usedVehiclesCompletedYesterday);
    totals.new_comp_yest += toNumber(item.newVehiclesCompletedYesterday);
    totals.nc_del_comp += toNumber(item.nc_deliveries_completed);
    totals.nc_preps_comp += toNumber(item.nc_lot_preps_completed);
  };

  const prepareDealerShipFinalReportSummaryData = data => {
    const summarizedData = {
      used_photos: 0,
      used_veh: 0,
      uc_det_queue: 0,
      uc_det_comp: 0,
      new_photos: 0,
      new_veh: 0,
      used_comp_yest: 0,
      new_comp_yest: 0,
      nc_del_comp: 0,
      nc_preps_comp: 0,
    };
    data.forEach(item => {
      updateTotals(summarizedData, item);
    });

    const used_percent =
      summarizedData.used_veh > 0
        ? `${(
            (summarizedData.used_photos / summarizedData.used_veh) *
            100
          ).toFixed(2)}%`
        : '0%';

    const new_percent =
      summarizedData.new_veh > 0
        ? `${(
            (summarizedData.new_photos / summarizedData.new_veh) *
            100
          ).toFixed(2)}%`
        : '0%';
    summarizedData.used_percent = used_percent;
    summarizedData.new_percent = new_percent;
    return summarizedData;
  };

  const processDDProAnalyticsData = (
    allDDProDealers,
    processedFlData,
    dealerShipMappingConfigData,
    usedCarDetails,
    ncLotPreps,
    ncDeliveries
  ) => {
    const resultArray = [];
    const groupingConfig = buildGroupingConfig(dealerShipMappingConfigData);

    // Track which ddpro dealers are already covered by FL data
    const coveredDdproIds = new Set();
    for (const item of processedFlData) {
      if (item.ddpro_dealer_ids) {
        const ids = item.ddpro_dealer_ids.split(',');
        ids.forEach(id => coveredDdproIds.add(id.trim()));
      }
    }

    // Create a set of ALL ddpro dealer IDs that exist in the mapping config
    const allMappedDdproIds = new Set();
    for (const mapping of dealerShipMappingConfigData) {
      if (mapping.ddproDealerId) {
        const ids = mapping.ddproDealerId.split(',').map(id => id.trim());
        ids.forEach(id => allMappedDdproIds.add(id));
      }
    }

    // Process each ddpro dealer from ALL_DDPRO_DEALERS
    const processedDealerIds = new Set();
    for (const dealer of allDDProDealers) {
      const dealerId = dealer.companyId;
      const dealerName = dealer.companyName;

      // Skip if already covered by FL data or already processed (same companyId, different departmentId)
      if (coveredDdproIds.has(dealerId) || processedDealerIds.has(dealerId)) {
        continue;
      }

      // Check if this dealer should be included (either in mapping config or has report data)
      const isInMapping = allMappedDdproIds.has(dealerId);
      const hasReportData =
        dealer.reportSummary &&
        (dealer.reportSummary.InQueueAsOfToday > 0 ||
          dealer.reportSummary.completedAsOfYesterday > 0 ||
          dealer.reportSummary.servicesTotal > 0);

      // Include if in mapping OR has report data
      if (isInMapping || hasReportData) {
        processedDealerIds.add(dealerId);
        const agg = aggregateDdproMetrics(
          [dealerId],
          usedCarDetails,
          ncLotPreps,
          ncDeliveries
        );
        resultArray.push({
          ff_dealer_id: '',
          ff_dealer: '',
          ddpro_dealer_ids: dealerId,
          pro_dd_dealer: dealerName,
          usedVehiclesWithoutPhotos: 'N/A',
          usedVehicleCount: 'N/A',
          used_percent: 'N/A',
          newVehiclesWithoutPhotos: 'N/A',
          newVehicleCount: 'N/A',
          new_percent: 'N/A',
          usedVehiclesCompletedYesterday: 'N/A',
          newVehiclesCompletedYesterday: 'N/A',
          uc_details_in_queue: agg.ucInQueue,
          uc_details_completed: agg.ucCompleted,
          nc_lot_preps_completed: agg.ncLotPreps,
          nc_deliveries_completed: agg.ncDeliveries,
        });
      }
    }

    // Group items by their ddpro dealer groups
    return groupByDdproGroups(resultArray, groupingConfig);
  };

  const buildGroupingConfig = dealerShipMappingConfigData => {
    const groups = new Map(); // key: group identifier, value: { ddproIds: [], flDealerIds: [], groupName: '' }

    for (const mapping of dealerShipMappingConfigData) {
      if (!mapping.ddproDealerId) continue;

      const ddproIds = mapping.ddproDealerId.split(',').map(id => id.trim());
      const ddproNames = (mapping.ddproDealerName || '')
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);
      const flDealerIds = (mapping.flDealerId || '')
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);
      const flDealerNames = (mapping.flDealerName || '')
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);

      // Create a group key based on the ddpro group (first ID or a combined key)
      const groupKey = ddproIds.join(',');

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          ddproIds: new Set(),
          flDealerIds: new Set(),
          flDealerNames: new Set(),
          groupName: ddproNames.join(',') || groupKey,
        });
      }

      const group = groups.get(groupKey);
      ddproIds.forEach(id => group.ddproIds.add(id));
      flDealerIds.forEach(id => group.flDealerIds.add(id));
      flDealerNames.forEach(name => group.flDealerNames.add(name));
    }

    // Convert Sets to Arrays
    const result = [];
    for (const [key, value] of groups) {
      result.push({
        groupId: key,
        groupName: value.groupName,
        ddproIds: Array.from(value.ddproIds),
        flDealerIds: Array.from(value.flDealerIds),
        flDealerNames: Array.from(value.flDealerNames),
      });
    }

    return result;
  };

  const buildFLMaps = mappingData => {
    const flToDdproMap = new Map();
    const flToNameMap = new Map();

    mappingData.forEach(mapping => {
      if (!mapping.flDealerId || !mapping.ddproDealerId) return;

      const flIds = mapping.flDealerId.split(',').map(id => id.trim());
      const flNames = (mapping.flDealerName || '')
        .split(',')
        .map(n => n.trim())
        .filter(Boolean);
      const ddproIds = mapping.ddproDealerId.split(',').map(id => id.trim());

      flIds.forEach((flId, i) => {
        if (!flToNameMap.has(flId)) {
          flToNameMap.set(flId, flNames[i] || flId);
        }

        if (!flToDdproMap.has(flId)) {
          flToDdproMap.set(flId, []);
        }

        flToDdproMap.get(flId).push(...ddproIds);
      });
    });

    return { flToDdproMap, flToNameMap };
  };

  const buildDdproNameMap = mappingData => {
    const map = new Map();

    mappingData.forEach(mapping => {
      if (!mapping.ddproDealerId) return;

      const ids = mapping.ddproDealerId.split(',').map(id => id.trim());
      const names = (mapping.ddproDealerName || '')
        .split(',')
        .map(n => n.trim())
        .filter(Boolean);

      ids.forEach((id, i) => {
        if (!map.has(id)) {
          map.set(id, names[i] || id);
        }
      });
    });

    return map;
  };

  const aggregateDdproMetrics = (
    ddproIds,
    usedCarDetails,
    ncLotPreps,
    ncDeliveries
  ) => {
    return ddproIds.reduce(
      (acc, id) => {
        if (usedCarDetails[id]) {
          acc.ucInQueue += usedCarDetails[id].uc_details_in_queue;
          acc.ucCompleted += usedCarDetails[id].uc_details_completed;
        }
        if (ncLotPreps[id]) {
          acc.ncLotPreps += ncLotPreps[id].nc_lot_preps_completed;
        }
        if (ncDeliveries[id]) {
          acc.ncDeliveries += ncDeliveries[id].nc_deliveries_completed;
        }
        return acc;
      },
      {
        ucInQueue: 0,
        ucCompleted: 0,
        ncLotPreps: 0,
        ncDeliveries: 0,
      }
    );
  };

  const buildResultItem = (
    flDealerId,
    item,
    flToNameMap,
    ddproIds,
    ddproNameMap
  ) => {
    const ddproNames = ddproIds.map(id => ddproNameMap.get(id));
    const dealerShipName = ddproNames.filter(Boolean).join(',');

    const emptyValue = dealerShipName ? 0 : 'N/A';

    return {
      ff_dealer_id: flDealerId,
      ff_dealer: flToNameMap.get(flDealerId) || item.dealershipName,
      ddpro_dealer_ids: ddproIds.join(','),
      pro_dd_dealer: dealerShipName,

      usedVehiclesWithoutPhotos: item.usedVehiclesWithoutPhotos,
      usedVehicleCount: item.usedVehicleCount,
      used_percent: calculatePercentage(
        item.usedVehiclesWithoutPhotos,
        item.usedVehicleCount
      ),

      newVehiclesWithoutPhotos: item.newVehiclesWithoutPhotos,
      newVehicleCount: item.newVehicleCount,
      new_percent: calculatePercentage(
        item.newVehiclesWithoutPhotos,
        item.newVehicleCount
      ),

      usedVehiclesCompletedYesterday: item.usedVehiclesCompletedYesterday,
      newVehiclesCompletedYesterday: item.newVehiclesCompletedYesterday,

      uc_details_in_queue: emptyValue,
      uc_details_completed: emptyValue,
      nc_lot_preps_completed: emptyValue,
      nc_deliveries_completed: emptyValue,
    };
  };

  const groupByDdproGroups = (items, groupingConfig) => {
    const resultArray = [];
    const processedIds = new Set();

    // Process each group
    for (const group of groupingConfig) {
      const groupItems = [];

      for (const item of items) {
        const itemDdproIds = item.ddpro_dealer_ids.split(',');

        // Check if this item belongs to the current group
        const belongsToGroup = itemDdproIds.some(id =>
          group.ddproIds.includes(id.trim())
        );

        if (belongsToGroup) {
          groupItems.push(item);
          itemDdproIds.forEach(id => processedIds.add(id.trim()));
        }
      }

      if (groupItems.length > 0) {
        const aggregated = {
          ff_dealer_id: group.flDealerIds.join(','),
          ff_dealer: group.flDealerNames.join(','),
          ddpro_dealer_ids: group.ddproIds.join(','),
          pro_dd_dealer: group.groupName,
          usedVehiclesWithoutPhotos: 'N/A',
          usedVehicleCount: 'N/A',
          used_percent: 'N/A',
          newVehiclesWithoutPhotos: 'N/A',
          newVehicleCount: 'N/A',
          new_percent: 'N/A',
          usedVehiclesCompletedYesterday: 'N/A',
          newVehiclesCompletedYesterday: 'N/A',
          uc_details_in_queue: arraySum(
            arrayColumn(groupItems, 'uc_details_in_queue')
          ),
          uc_details_completed: arraySum(
            arrayColumn(groupItems, 'uc_details_completed')
          ),
          nc_lot_preps_completed: arraySum(
            arrayColumn(groupItems, 'nc_lot_preps_completed')
          ),
          nc_deliveries_completed: arraySum(
            arrayColumn(groupItems, 'nc_deliveries_completed')
          ),
        };
        resultArray.push(aggregated);
      }
    }

    // Add items that weren't in any group
    for (const item of items) {
      const itemDdproIds = item.ddpro_dealer_ids.split(',');
      const isProcessed = itemDdproIds.some(id => processedIds.has(id.trim()));

      if (!isProcessed && item.ddpro_dealer_ids) {
        resultArray.push(item);
      }
    }

    return resultArray;
  };

  const processResult = (
    resultArray,
    dealerShipMappingConfigData,
    usedCarDetails,
    ncLotPreps,
    ncDeliveries
  ) => {
    const groupingConfig = buildGroupingConfig(dealerShipMappingConfigData);

    const ddproToGroupMap = buildDdproLookup(groupingConfig);
    const groupsMap = mapItemsToGroups(resultArray, ddproToGroupMap);

    const { mergedResults, mergedIds } = buildMergedResults(
      groupsMap,
      usedCarDetails,
      ncLotPreps,
      ncDeliveries
    );

    return appendUnmergedItems(resultArray, mergedResults, mergedIds);
  };

  const buildDdproLookup = groupingConfig => {
    const map = new Map();

    for (const group of groupingConfig) {
      if (group.flDealerIds.length <= 1) continue;

      for (const id of group.ddproIds) {
        map.set(id.trim(), group);
      }
    }

    return map;
  };

  const mapItemsToGroups = (resultArray, ddproToGroupMap) => {
    const groupsMap = new Map();

    for (const item of resultArray) {
      if (!item.ff_dealer_id || !item.ddpro_dealer_ids) continue;
      const ddproIds = item.ddpro_dealer_ids.split(',');
      let group = null;
      for (const id of ddproIds) {
        const found = ddproToGroupMap.get(id.trim());
        if (found) {
          group = found;
          break;
        }
      }
      if (!group) continue;
      if (!groupsMap.has(group.groupId)) {
        groupsMap.set(group.groupId, { group, items: [] });
      }
      groupsMap.get(group.groupId).items.push(item);
    }

    return groupsMap;
  };

  const buildMergedResults = (
    groupsMap,
    usedCarDetails,
    ncLotPreps,
    ncDeliveries
  ) => {
    const mergedResults = [];
    const mergedIds = new Set();

    for (const { group, items } of groupsMap.values()) {
      if (items.length === 1) {
        mergedResults.push(items[0]);
        mergedIds.add(items[0].ff_dealer_id);
        continue;
      }

      const merged = createMergedItem(
        group,
        items,
        usedCarDetails,
        ncLotPreps,
        ncDeliveries
      );

      items.forEach(i => i.ff_dealer_id && mergedIds.add(i.ff_dealer_id));
      mergedResults.push(merged);
    }

    return { mergedResults, mergedIds };
  };

  const createMergedItem = (
    group,
    items,
    usedCarDetails,
    ncLotPreps,
    ncDeliveries
  ) => {
    const sum = key => arraySum(arrayColumn(items, key));
    const agg = aggregateDdproMetrics(
      group.ddproIds,
      usedCarDetails,
      ncLotPreps,
      ncDeliveries
    );

    const merged = {
      ff_dealer_id: group.flDealerIds.join(','),
      ff_dealer: group.flDealerNames.join(','),
      ddpro_dealer_ids: group.ddproIds.join(','),
      pro_dd_dealer: group.groupName,

      usedVehiclesWithoutPhotos: sum('usedVehiclesWithoutPhotos'),
      usedVehicleCount: sum('usedVehicleCount'),
      newVehiclesWithoutPhotos: sum('newVehiclesWithoutPhotos'),
      newVehicleCount: sum('newVehicleCount'),
      usedVehiclesCompletedYesterday: sum('usedVehiclesCompletedYesterday'),
      newVehiclesCompletedYesterday: sum('newVehiclesCompletedYesterday'),
      uc_details_in_queue: agg.ucInQueue,
      uc_details_completed: agg.ucCompleted,
      nc_lot_preps_completed: agg.ncLotPreps,
      nc_deliveries_completed: agg.ncDeliveries,
    };

    return addPercentages(merged);
  };

  const addPercentages = item => {
    const calc = (num, den) =>
      den > 0 ? `${Math.round((num / den) * 10000) / 100}%` : '0%';

    return {
      ...item,
      used_percent: calc(item.usedVehiclesWithoutPhotos, item.usedVehicleCount),
      new_percent: calc(item.newVehiclesWithoutPhotos, item.newVehicleCount),
    };
  };

  const appendUnmergedItems = (resultArray, mergedResults, mergedIds) => {
    const remaining = [...mergedResults];

    for (const item of resultArray) {
      if (!item.ff_dealer_id || !mergedIds.has(item.ff_dealer_id)) {
        remaining.push(item);
      }
    }

    return remaining;
  };

  const extractExistingFLIds = resultArray => {
    const existingFLIds = new Set();

    resultArray.forEach(item => {
      if (item.ff_dealer_id) {
        item.ff_dealer_id.split(',').forEach(id => {
          existingFLIds.add(id.trim());
        });
      }
    });

    return existingFLIds;
  };

  const buildFLNameMap = dealerShipMappingConfigData => {
    const flNameMap = new Map();

    dealerShipMappingConfigData.forEach(mapping => {
      if (mapping.flDealerId && mapping.flDealerName) {
        const ids = mapping.flDealerId.split(',').map(id => id.trim());
        const names = mapping.flDealerName.split(',').map(n => n.trim());

        ids.forEach((id, index) => {
          if (!flNameMap.has(id)) {
            flNameMap.set(id, names[index]);
          }
        });
      }
    });

    return flNameMap;
  };

  const collectAllFLIds = (mappingData, analyticsData) => {
    const allFLIds = new Set(Object.keys(analyticsData));

    mappingData.forEach(mapping => {
      if (mapping.flDealerId) {
        mapping.flDealerId
          .split(',')
          .map(id => id.trim())
          .forEach(id => allFLIds.add(id));
      }
    });

    return allFLIds;
  };

  const calculatePercentage = (num, total) => {
    if (!total) return '0%';
    return `${Math.round((num / total) * 10000) / 100}%`;
  };

  const buildDealerObject = (flId, item, flNameMap) => ({
    ff_dealer_id: flId,
    ff_dealer: flNameMap.get(flId) || item.dealershipName,
    ddpro_dealer_ids: '',
    pro_dd_dealer: '',
    usedVehiclesWithoutPhotos: item.usedVehiclesWithoutPhotos,
    usedVehicleCount: item.usedVehicleCount,
    used_percent: calculatePercentage(
      item.usedVehiclesWithoutPhotos,
      item.usedVehicleCount
    ),
    newVehiclesWithoutPhotos: item.newVehiclesWithoutPhotos,
    newVehicleCount: item.newVehicleCount,
    new_percent: calculatePercentage(
      item.newVehiclesWithoutPhotos,
      item.newVehicleCount
    ),
    usedVehiclesCompletedYesterday: item.usedVehiclesCompletedYesterday,
    newVehiclesCompletedYesterday: item.newVehiclesCompletedYesterday,
    uc_details_in_queue: 'N/A',
    uc_details_completed: 'N/A',
    nc_lot_preps_completed: 'N/A',
    nc_deliveries_completed: 'N/A',
  });

  const ensureAllFLDealersRepresented = (
    resultArray,
    dealerShipMappingConfigData,
    flPhotoCountAnalyticsData
  ) => {
    const existingFLIds = extractExistingFLIds(resultArray);
    const flNameMap = buildFLNameMap(dealerShipMappingConfigData);
    const allFLIds = collectAllFLIds(
      dealerShipMappingConfigData,
      flPhotoCountAnalyticsData
    );

    allFLIds.forEach(flId => {
      const item = flPhotoCountAnalyticsData[flId];

      if (!existingFLIds.has(flId) && item) {
        resultArray.push(buildDealerObject(flId, item, flNameMap));
      }
    });

    return resultArray;
  };

  const arraySum = arr => {
    return arr.reduce((s, v) => s + (Number(v) || 0), 0);
  };

  const arrayColumn = (arr, col) => {
    return arr.map(r => r[col]);
  };

  const processFlPhotoCountAnalyticsData = (
    flPhotoCountAnalyticsData,
    dealerShipMappingConfigData,
    usedCarDetails,
    ncLotPreps,
    ncDeliveries
  ) => {
    const { flToDdproMap, flToNameMap } = buildFLMaps(
      dealerShipMappingConfigData
    );

    const ddproNameMap = buildDdproNameMap(dealerShipMappingConfigData);

    return Object.entries(flPhotoCountAnalyticsData).map(
      ([flDealerId, item]) => {
        const ddproIds = flToDdproMap.get(flDealerId) || [];

        const result = buildResultItem(
          flDealerId,
          item,
          flToNameMap,
          ddproIds,
          ddproNameMap
        );

        if (ddproIds.length && result.pro_dd_dealer) {
          const agg = aggregateDdproMetrics(
            ddproIds,
            usedCarDetails,
            ncLotPreps,
            ncDeliveries
          );

          result.uc_details_in_queue = agg.ucInQueue;
          result.uc_details_completed = agg.ucCompleted;
          result.nc_lot_preps_completed = agg.ncLotPreps;
          result.nc_deliveries_completed = agg.ncDeliveries;
        }

        return result;
      }
    );
  };

  const getDealershipMappingConfig = useCallback(async dealershipId => {
    const endpoint = EndPoints.REPORT.DEALERSHIP_MAPPING.ENDPOINT.replace(
      '{dealershipId}',
      dealershipId
    );
    return await amplifyApi(
      {},
      endpoint,
      EndPoints.REPORT.DEALERSHIP_MAPPING.METHOD,
      true
    );
  }, []);

  const getFlPhotoCountAnalytics = useCallback(async frontlineDealerId => {
    const endpoint = EndPoints.REPORT.FRONTLINE_ANALYTICS.ENDPOINT.replace(
      '{frontlineDealerId}',
      frontlineDealerId
    );
    return await amplifyApi(
      {},
      endpoint,
      EndPoints.REPORT.FRONTLINE_ANALYTICS.METHOD,
      true
    );
  }, []);

  const getAllDDProDealers = useCallback(async dealershipId => {
    // /orders/summary/dealer/{companyId}?departments=Used Car Sales
    const endpoint = `${EndPoints.REPORT.DDPRO_ANALYTICS.ENDPOINT.replace(
      '{companyId}',
      dealershipId
    )}?departments=Used Car Sales`;
    return (
      (
        await amplifyApi(
          {},
          endpoint,
          EndPoints.REPORT.DDPRO_ANALYTICS.METHOD,
          true
        )
      )?.data?.dealers ?? []
    );
  }, []);

  const getUsedCarDetail = useCallback(async dealershipId => {
    // ?departments=Used Car Sales&services=Used Car Detail
    const endpoint = `${EndPoints.REPORT.DDPRO_ANALYTICS.ENDPOINT.replace(
      '{companyId}',
      dealershipId
    )}?departments=Used Car Sales&services=Used Vehicle Detail`;
    return (
      (
        await amplifyApi(
          {},
          endpoint,
          EndPoints.REPORT.DDPRO_ANALYTICS.METHOD,
          true
        )
      )?.data?.dealers ?? []
    );
  }, []);

  const getNCLotPrepsCompleted = useCallback(async dealershipId => {
    // ?departments=Used Car Sales&services=New Car Lot Prep,Lot Prep / Lot Wash,New Vehicle Delivery (Lot Prepped)'
    const endpoint = `${EndPoints.REPORT.DDPRO_ANALYTICS.ENDPOINT.replace(
      '{companyId}',
      dealershipId
    )}?departments=New Car Sales&services=New Vehicle Lot Prep,New Vehicle Delivery (Lot Prep)`;
    return (
      (
        await amplifyApi(
          {},
          endpoint,
          EndPoints.REPORT.DDPRO_ANALYTICS.METHOD,
          true
        )
      )?.data?.dealers ?? []
    );
  }, []);

  const getNCDeliveriesCompleted = useCallback(async dealershipId => {
    // ?departments=Used Car Sales&services=New Vehicle Delivery,New Car Delivery
    const endpoint = `${EndPoints.REPORT.DDPRO_ANALYTICS.ENDPOINT.replace(
      '{companyId}',
      dealershipId
    )}?departments=New Car Sales&services=New Vehicle Delivery,New Car Delivery`;
    return (
      (
        await amplifyApi(
          {},
          endpoint,
          EndPoints.REPORT.DDPRO_ANALYTICS.METHOD,
          true
        )
      )?.data?.dealers ?? []
    );
  }, []);

  const fetchReport = async dealershipId => {
    const dealerShipMappingConfigData = (
      await getDealershipMappingConfig(dealershipId)
    ).data;
    const [firstRecord] = dealerShipMappingConfigData;
    const frontlineDealerId = firstRecord?.flDealerGroupId;

    const flPhotoCountAnalyticsData = (
      await getFlPhotoCountAnalytics(frontlineDealerId)
    ).data;

    const [allDDProDealers, usedCarDetailsRaw, ncLotPrepsRaw, ncDeliveriesRaw] =
      await Promise.all([
        getAllDDProDealers(dealershipId),
        getUsedCarDetail(dealershipId),
        getNCLotPrepsCompleted(dealershipId),
        getNCDeliveriesCompleted(dealershipId),
      ]);

    const usedCarDetails = usedCarDetailsRaw.reduce((acc, d) => {
      if (!acc[d.companyId]) {
        acc[d.companyId] = { uc_details_in_queue: 0, uc_details_completed: 0 };
      }
      acc[d.companyId].uc_details_in_queue +=
        d.reportSummary?.InQueueAsOfToday || 0;
      acc[d.companyId].uc_details_completed +=
        d.reportSummary?.completedAsOfYesterday || 0;
      return acc;
    }, {});

    const ncLotPreps = ncLotPrepsRaw.reduce((acc, d) => {
      if (!acc[d.companyId]) {
        acc[d.companyId] = { nc_lot_preps_completed: 0 };
      }
      acc[d.companyId].nc_lot_preps_completed +=
        d.reportSummary?.completedAsOfYesterday || 0;
      return acc;
    }, {});

    const ncDeliveries = ncDeliveriesRaw.reduce((acc, d) => {
      if (!acc[d.companyId]) {
        acc[d.companyId] = { nc_deliveries_completed: 0 };
      }
      acc[d.companyId].nc_deliveries_completed +=
        d.reportSummary?.completedAsOfYesterday || 0;
      return acc;
    }, {});

    const processedFlData = processFlPhotoCountAnalyticsData(
      flPhotoCountAnalyticsData,
      dealerShipMappingConfigData,
      usedCarDetails,
      ncLotPreps,
      ncDeliveries
    );

    const processedDDProData = processDDProAnalyticsData(
      allDDProDealers,
      processedFlData,
      dealerShipMappingConfigData,
      usedCarDetails,
      ncLotPreps,
      ncDeliveries
    );

    const mergedData = [...processedFlData, ...processedDDProData];
    let consolidatedReportsData = processResult(
      mergedData,
      dealerShipMappingConfigData,
      usedCarDetails,
      ncLotPreps,
      ncDeliveries
    );

    consolidatedReportsData = ensureAllFLDealersRepresented(
      consolidatedReportsData,
      dealerShipMappingConfigData,
      flPhotoCountAnalyticsData
    );

    const summaryRowData = prepareDealerShipFinalReportSummaryData(
      consolidatedReportsData
    );

    return {
      reportsData: consolidatedReportsData,
      summaryData: summaryRowData,
    };
  };

  const fetchDownloadableReport = useCallback(async report => {
    return await amplifyApi(
      {
        reportsData: report?.reportsData,
        summaryData: report?.summaryData,
      },
      EndPoints.REPORT.DOWNLOAD_DEALERSHIP_SUMMARY_REPORT.ENDPOINT,
      EndPoints.REPORT.DOWNLOAD_DEALERSHIP_SUMMARY_REPORT.METHOD,
      true
    );
  }, []);

  const fetchReportRef = useRef(fetchReport);
  fetchReportRef.current = fetchReport;

  const loadReport = useCallback(async dealershipId => {
    if (!dealershipId) return;

    try {
      setError(null);

      setLoading(true);

      const response = await fetchReportRef.current(dealershipId);

      setReportsData(response.reportsData);
      setSummaryData(response.summaryData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch DealershipSummaryReport:', err);
      setError('Failed to fetch DealershipSummaryReport.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(dealershipGroupId);
  }, [loadReport, dealershipGroupId]);

  const handleRefresh = () => {
    loadReport(dealershipGroupId);
  };

  const handleDownloadReport = async () => {
    if (!dealershipGroupId) return;

    try {
      setDownloading(true);
      setShowSnackbar({
        open: true,
        text: 'dealershipReportDownloadInfo',
        type: 'success',
      });

      const report = await fetchReportRef.current(dealershipGroupId);
      const response = await fetchDownloadableReport(report);
      const downloadableUrl = response?.data?.dealershipReportDownloadUrl;

      if (downloadableUrl) {
        DownloadURL(downloadableUrl, 'dealership-summary-report', 'xlsx');
      } else {
        setShowSnackbar({
          open: true,
          text: 'errorSnackbar',
          type: 'error',
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Download failed:', err);
      setShowSnackbar({
        open: true,
        text: 'errorSnackbar',
        type: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack py={3.5} px={3} gap={4}>
      <GenSnackBar
        title={t(showSnackbar.text)}
        type={showSnackbar.type}
        open={showSnackbar.open}
        onClose={() =>
          setShowSnackbar({
            open: false,
            text: '',
            type: '',
          })
        }
      />
      {/* Header */}
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">{t('laFontaineReport')}</Typography>

        <Button
          variant="outlined"
          onClick={handleDownloadReport}
          disabled={downloading}
          sx={{ minWidth: 36, p: 0, minHeight: 36 }}
        >
          {downloading ? <CircularProgress size={18} /> : <Download />}
        </Button>
      </Stack>

      <Paper elevation={2} sx={{ p: 3 }}>
        <InfoTableDialog
          open={showInfoTable}
          onClose={() => setShowInfoTable(false)}
        />

        {/* Section Header */}
        <Stack direction="row" justifyContent="space-between" mb={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">{t('laFontaineDailyReport')}</Typography>

            <IconButton
              onClick={() => setShowInfoTable(true)}
              sx={{ color: theme => theme.palette.grey[500] }}
            >
              <InfoOutlined />
            </IconButton>
          </Stack>

          <Stack direction="row" alignItems="center">
            <RefreshButton onClick={handleRefresh} loading={loading} />
          </Stack>
        </Stack>

        {/* Table */}
        <MultiHeaderTable
          data={reportsData}
          summaryData={summaryData}
          loading={loading}
          error={error}
        />
      </Paper>
    </Stack>
  );
}
